import React, { useState, useEffect } from 'react';
import './CompanyProfile.css';

const CompanyProfile = ({ isOpen, onClose, onSave, existingProfile, isRequired }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    primaryContact: '',
    phoneNumber: '',
    email: '',
    website: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existingProfile) {
      setFormData({
        companyName: existingProfile.companyName || '',
        primaryContact: existingProfile.primaryContact || '',
        phoneNumber: existingProfile.phoneNumber || '',
        email: existingProfile.email || '',
        website: existingProfile.website || ''
      });
    }
  }, [existingProfile]);

  const validateForm = () => {
    const newErrors = {};

    // Company Name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    // Primary Contact validation
    if (!formData.primaryContact.trim()) {
      newErrors.primaryContact = 'Primary contact name is required';
    }

    // Phone Number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Website validation
    if (!formData.website.trim()) {
      newErrors.website = 'Website URL is required';
    } else if (!/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL (include http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isRequired) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay">
      <div className="profile-modal">
        <div className="profile-modal-header">
          <h2>Company Profile</h2>
          {!isRequired && (
            <button 
              className="close-button" 
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>

        {isRequired && (
          <div className="profile-required-notice">
            <span>⚠️ Please complete your company profile to access all portal features</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="companyName">
              Company Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              className={errors.companyName ? 'error' : ''}
              placeholder="Enter your company name"
            />
            {errors.companyName && (
              <span className="error-message">{errors.companyName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="primaryContact">
              Primary Contact <span className="required">*</span>
            </label>
            <input
              type="text"
              id="primaryContact"
              name="primaryContact"
              value={formData.primaryContact}
              onChange={handleInputChange}
              className={errors.primaryContact ? 'error' : ''}
              placeholder="Enter primary contact name"
            />
            {errors.primaryContact && (
              <span className="error-message">{errors.primaryContact}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">
              Phone Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              className={errors.phoneNumber ? 'error' : ''}
              placeholder="Enter phone number"
            />
            {errors.phoneNumber && (
              <span className="error-message">{errors.phoneNumber}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter email address"
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="website">
              Website <span className="required">*</span>
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className={errors.website ? 'error' : ''}
              placeholder="https://www.yourcompany.com"
            />
            {errors.website && (
              <span className="error-message">{errors.website}</span>
            )}
          </div>

          <div className="profile-form-actions">
            <button
              type="submit"
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Profile'}
            </button>
            {!isRequired && (
              <button
                type="button"
                className="cancel-button"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyProfile;
